import { computed, unref, Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";

interface TranscriptResponse {
  callSid: string;
  text: string;
}

/**
 * Load transcript data using TanStack Query
 */
export function useTranscriptQuery(transcriptUrl: Ref<string | null> | string | null) {
  const queryKey = computed(() => {
    const url = unref(transcriptUrl);
    if (!url) return ["transcript", "disabled"];
    return ["transcript", url];
  });

  const enabled = computed(() => {
    const url = unref(transcriptUrl);
    return url !== null && url !== undefined && url !== "";
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const url = unref(transcriptUrl);
      if (!url) throw new Error("Transcript URL is required");

      const response = await fetch(url);

      if (!response.ok) {
        if (response.status === 403 || response.status === 401) {
          throw new Error("Transcript link has expired. Click Retry to reload.");
        }
        throw new Error(`Failed to fetch transcript: ${response.statusText}`);
      }

      const data: TranscriptResponse = await response.json();

      if (!data || typeof data.text !== "string") {
        throw new Error("Invalid transcript format: missing text field");
      }

      return data.text;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes("expired")) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
